import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet, WalletMultiButton } from '../context/SimpleWalletContext';

interface SimulationProject {
    id: number;
    name: string;
    description: string;
    targetAmount: number;
    raisedAmount: number;
    investorCount: number;
    isActive: boolean;
    progress: number;
}

interface SimulationInvestment {
    id: string;
    projectId: number;
    investor: string;
    amount: number;
    timestamp: number;
    status: 'pending' | 'confirmed' | 'failed';
}

interface SimulationUser {
    name: string;
    balance: number;
    investments: SimulationInvestment[];
}

interface SimulationData {
    projects: SimulationProject[];
    users: SimulationUser[];
    investments: SimulationInvestment[];
    summary: {
        totalProjects: number;
        totalInvestments: number;
        totalAmountRaised: number;
        totalUsers: number;
    };
}

const SolanaSimulation: React.FC = () => {
    const { connected, publicKey, balance, updateBalance } = useWallet();
    const [simulationData, setSimulationData] = useState<SimulationData | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [currentScenario, setCurrentScenario] = useState<string>('');
    const [logs, setLogs] = useState<string[]>([]);
    const [selectedProject, setSelectedProject] = useState<number | null>(null);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [showInvestmentModal, setShowInvestmentModal] = useState(false);
    const [investmentAmount, setInvestmentAmount] = useState<number>(0);
    const [selectedProjectForInvestment, setSelectedProjectForInvestment] = useState<number | null>(null);

    // Mock simulation data
    const mockData: SimulationData = {
        projects: [
            {
                id: 1,
                name: "Solar Farm Karachi",
                description: "50MW solar farm in Karachi, Pakistan",
                targetAmount: 1000,
                raisedAmount: 425,
                investorCount: 4,
                isActive: true,
                progress: 42.5
            },
            {
                id: 2,
                name: "Wind Energy Lahore",
                description: "30MW wind farm near Lahore",
                targetAmount: 800,
                raisedAmount: 225,
                investorCount: 2,
                isActive: true,
                progress: 28.1
            },
            {
                id: 3,
                name: "Hydroelectric Gilgit",
                description: "20MW hydroelectric plant in Gilgit",
                targetAmount: 600,
                raisedAmount: 200,
                investorCount: 2,
                isActive: true,
                progress: 33.3
            }
        ],
        users: [
            { name: "Alice", balance: 750, investments: [] },
            { name: "Bob", balance: 450, investments: [] },
            { name: "Charlie", balance: 675, investments: [] },
            { name: "Diana", balance: 1000, investments: [] },
            { name: "Eve", balance: 150, investments: [] }
        ],
        investments: [],
        summary: {
            totalProjects: 3,
            totalInvestments: 8,
            totalAmountRaised: 850,
            totalUsers: 5
        }
    };

    const addLog = (message: string) => {
        setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    };

    const simulateInvestment = async (userName: string, projectId: number, amount: number) => {
        addLog(`👤 ${userName} attempting to invest ${amount} SOL in Project ${projectId}...`);

        // Simulate transaction delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        const project = simulationData?.projects.find(p => p.id === projectId);

        if (!project) {
            addLog(`❌ Investment failed: Project not found`);
            return;
        }

        if (project.raisedAmount + amount > project.targetAmount) {
            addLog(`❌ Investment failed: Would exceed target amount`);
            return;
        }

        if (amount < 0.1) {
            addLog(`❌ Investment failed: Below minimum amount (0.1 SOL)`);
            return;
        }

        // Update simulation data
        const newInvestment: SimulationInvestment = {
            id: `inv_${Date.now()}`,
            projectId,
            investor: userName,
            amount,
            timestamp: Date.now(),
            status: 'confirmed'
        };

        setSimulationData(prev => {
            if (!prev) return prev;

            const updated = { ...prev };
            updated.investments.push(newInvestment);

            // Update project
            const projectIndex = updated.projects.findIndex(p => p.id === projectId);
            if (projectIndex !== -1) {
                updated.projects[projectIndex].raisedAmount += amount;
                updated.projects[projectIndex].investorCount += 1;
                updated.projects[projectIndex].progress =
                    (updated.projects[projectIndex].raisedAmount / updated.projects[projectIndex].targetAmount) * 100;
            }

            updated.summary.totalInvestments += 1;
            updated.summary.totalAmountRaised += amount;

            return updated;
        });

        addLog(`✅ Investment successful: ${userName} invested ${amount} SOL in ${project.name}`);
    };

    const runScenario = async (scenarioName: string, scenario: () => Promise<void>) => {
        setIsRunning(true);
        setCurrentScenario(scenarioName);
        setLogs([]);

        addLog(`🎬 Starting scenario: ${scenarioName}`);

        try {
            await scenario();
            addLog(`✅ Scenario completed successfully`);
        } catch (error) {
            addLog(`❌ Scenario failed: ${error}`);
        } finally {
            setIsRunning(false);
            setCurrentScenario('');
        }
    };

    const scenario1_BasicInvestment = async () => {
        await simulateInvestment("Alice", 1, 100);
        await simulateInvestment("Bob", 1, 50);
        await simulateInvestment("Charlie", 1, 75);
    };

    const scenario2_MultipleProjects = async () => {
        await simulateInvestment("Diana", 2, 150);
        await simulateInvestment("Eve", 2, 100);
        await simulateInvestment("Alice", 3, 80);
        await simulateInvestment("Bob", 3, 120);
    };

    const scenario3_EdgeCases = async () => {
        await simulateInvestment("Alice", 1, 0.05); // Below minimum
        await simulateInvestment("Bob", 1, 2000); // Exceeds target
        await simulateInvestment("Eve", 2, 500); // Insufficient balance
        await simulateInvestment("Charlie", 3, 0.1); // Valid minimum
    };

    const scenario4_RealWorld = async () => {
        const investments = [
            { user: "Alice", project: 1, amount: 150 },
            { user: "Bob", project: 2, amount: 75 },
            { user: "Charlie", project: 1, amount: 100 },
            { user: "Diana", project: 3, amount: 200 },
            { user: "Eve", project: 2, amount: 125 },
            { user: "Alice", project: 2, amount: 50 },
            { user: "Bob", project: 3, amount: 80 },
            { user: "Charlie", project: 1, amount: 100 }
        ];

        for (const investment of investments) {
            await simulateInvestment(investment.user, investment.project, investment.amount);
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    useEffect(() => {
        setSimulationData(mockData);
    }, []);

    if (!simulationData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-900 via-blue-900 to-purple-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-white font-mono">Loading Solana Simulation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        🚀 Solana Integration Simulation
                    </h1>
                    <p className="text-gray-600 mb-4">
                        Real-time demonstration of Greenova8's blockchain investment platform
                    </p>
                    <div className="flex justify-center">
                        <WalletMultiButton className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105" />
                    </div>
                    {connected && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg"
                        >
                            <div className="text-center">
                                <p className="text-green-700 text-sm mb-2">
                                    ✅ Wallet Connected: {publicKey?.toBase58().slice(0, 8)}...{publicKey?.toBase58().slice(-8)}
                                </p>
                                <div className="text-green-600 font-bold text-xl">{balance} SOL</div>
                                <div className="text-gray-600 text-xs">Available Balance</div>
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Summary Cards */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
                >
                    {[
                        { label: "Total Projects", value: simulationData.summary.totalProjects, icon: "🏗️" },
                        { label: "Total Users", value: simulationData.summary.totalUsers, icon: "👥" },
                        { label: "Total Investments", value: simulationData.summary.totalInvestments, icon: "💰" },
                        { label: "Amount Raised", value: `${simulationData.summary.totalAmountRaised} SOL`, icon: "📈" }
                    ].map((item, index) => (
                        <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                            <div className="text-3xl mb-2">{item.icon}</div>
                            <div className="text-2xl font-bold text-gray-900">{item.value}</div>
                            <div className="text-gray-600 text-sm">{item.label}</div>
                        </div>
                    ))}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Projects Panel */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="lg:col-span-2"
                    >
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Investment Projects</h2>

                            <div className="space-y-4">
                                {simulationData.projects.map((project) => (
                                    <motion.div
                                        key={project.id}
                                        whileHover={{ scale: 1.02 }}
                                        className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => setSelectedProject(project.id)}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                                                <p className="text-gray-600 text-sm">{project.description}</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-green-600 font-bold">{project.raisedAmount} SOL</div>
                                                <div className="text-gray-600 text-sm">of {project.targetAmount} SOL</div>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                                                <span>Progress</span>
                                                <span>{project.progress.toFixed(1)}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <motion.div
                                                    className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full"
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${project.progress}%` }}
                                                    transition={{ duration: 1, delay: 0.5 }}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center text-sm text-gray-600">
                                            <span>👥 {project.investorCount} investors</span>
                                            <div className="flex gap-2">
                                                {connected && (
                                                    <button
                                                        onClick={() => {
                                                            console.log('Investment button clicked for project:', project.id);
                                                            setSelectedProjectForInvestment(project.id);
                                                            setShowInvestmentModal(true);
                                                            console.log('Modal should be showing now');
                                                        }}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors"
                                                    >
                                                        💰 Invest
                                                    </button>
                                                )}
                                                <span className={project.isActive ? "text-green-600" : "text-red-600"}>
                                                    {project.isActive ? "🟢 Active" : "🔴 Inactive"}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Control Panel */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-6"
                    >
                        {/* Scenario Controls */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                            <h2 className="text-xl font-bold text-white mb-4">🎮 Simulation Controls</h2>

                            {connected && (
                                <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg">
                                    <p className="text-green-300 text-sm mb-2">💰 Your Balance: {balance} SOL</p>
                                    <button
                                        onClick={() => {
                                            setSelectedProjectForInvestment(1);
                                            setShowInvestmentModal(true);
                                        }}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors"
                                    >
                                        🧪 Test Investment Modal
                                    </button>
                                </div>
                            )}

                            <div className="space-y-3">
                                <button
                                    onClick={() => runScenario("Basic Investment Flow", scenario1_BasicInvestment)}
                                    disabled={isRunning}
                                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors"
                                >
                                    📊 Basic Investment Flow
                                </button>

                                <button
                                    onClick={() => runScenario("Multiple Projects", scenario2_MultipleProjects)}
                                    disabled={isRunning}
                                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors"
                                >
                                    🏗️ Multiple Projects
                                </button>

                                <button
                                    onClick={() => runScenario("Edge Cases", scenario3_EdgeCases)}
                                    disabled={isRunning}
                                    className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors"
                                >
                                    ⚠️ Edge Cases
                                </button>

                                <button
                                    onClick={() => runScenario("Real-World Simulation", scenario4_RealWorld)}
                                    disabled={isRunning}
                                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors"
                                >
                                    🌍 Real-World Simulation
                                </button>
                            </div>

                            {isRunning && (
                                <div className="mt-4 text-center">
                                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                    <p className="text-blue-200 text-sm">Running: {currentScenario}</p>
                                </div>
                            )}
                        </div>

                        {/* Users Panel */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                            <h2 className="text-xl font-bold text-white mb-4">👥 Users</h2>

                            <div className="space-y-3">
                                {simulationData.users.map((user) => (
                                    <div
                                        key={user.name}
                                        className="bg-white/5 rounded-lg p-3 cursor-pointer hover:bg-white/10 transition-colors"
                                        onClick={() => setSelectedUser(user.name)}
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="text-white font-medium">{user.name}</span>
                                            <span className="text-green-400 font-bold">{user.balance} SOL</span>
                                        </div>
                                        <div className="text-blue-200 text-sm">
                                            {user.investments.length} investments
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Activity Log */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20"
                >
                    <h2 className="text-xl font-bold text-white mb-4">📝 Activity Log</h2>

                    <div className="bg-black/20 rounded-lg p-4 h-64 overflow-y-auto">
                        <AnimatePresence>
                            {logs.map((log, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className="text-green-300 font-mono text-sm mb-1"
                                >
                                    {log}
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </motion.div>

                {/* Project Detail Modal */}
                <AnimatePresence>
                    {selectedProject && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                            onClick={() => setSelectedProject(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-md w-full"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {(() => {
                                    const project = simulationData.projects.find(p => p.id === selectedProject);
                                    if (!project) return null;

                                    return (
                                        <>
                                            <h3 className="text-2xl font-bold text-white mb-4">{project.name}</h3>
                                            <p className="text-blue-200 mb-4">{project.description}</p>

                                            <div className="space-y-3">
                                                <div className="flex justify-between">
                                                    <span className="text-blue-200">Target Amount:</span>
                                                    <span className="text-white font-bold">{project.targetAmount} SOL</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-blue-200">Raised Amount:</span>
                                                    <span className="text-green-400 font-bold">{project.raisedAmount} SOL</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-blue-200">Investors:</span>
                                                    <span className="text-white font-bold">{project.investorCount}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-blue-200">Status:</span>
                                                    <span className={project.isActive ? "text-green-400" : "text-red-400"}>
                                                        {project.isActive ? "Active" : "Inactive"}
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setSelectedProject(null)}
                                                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                                            >
                                                Close
                                            </button>
                                        </>
                                    );
                                })()}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Investment Modal */}
                <AnimatePresence>
                    {showInvestmentModal && (() => {
                        console.log('Rendering investment modal, selectedProject:', selectedProjectForInvestment);
                        return (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                                onClick={() => setShowInvestmentModal(false)}
                            >
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-md w-full"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <h3 className="text-2xl font-bold text-white mb-4">💰 Make Investment</h3>

                                    {(() => {
                                        const project = simulationData?.projects.find(p => p.id === selectedProjectForInvestment);
                                        if (!project) return null;

                                        return (
                                            <>
                                                <div className="mb-4">
                                                    <p className="text-blue-200 mb-2">Project: <span className="text-white font-semibold">{project.name}</span></p>
                                                    <p className="text-blue-200 text-sm mb-4">{project.description}</p>
                                                    <div className="bg-white/5 rounded-lg p-3 mb-4">
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-blue-200">Target:</span>
                                                            <span className="text-white">{project.targetAmount} SOL</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-blue-200">Raised:</span>
                                                            <span className="text-green-400">{project.raisedAmount} SOL</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-blue-200">Remaining:</span>
                                                            <span className="text-yellow-400">{project.targetAmount - project.raisedAmount} SOL</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="block text-blue-200 text-sm">Investment Amount (SOL)</label>
                                                        <span className="text-green-400 text-sm">Balance: {balance} SOL</span>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        min="0.1"
                                                        max={Math.min(project.targetAmount - project.raisedAmount, balance)}
                                                        step="0.1"
                                                        value={investmentAmount}
                                                        onChange={(e) => setInvestmentAmount(parseFloat(e.target.value) || 0)}
                                                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-blue-300 focus:outline-none focus:border-blue-500"
                                                        placeholder="Enter amount in SOL"
                                                    />
                                                    <div className="flex justify-between text-xs mt-1">
                                                        <span className="text-blue-300">Minimum: 0.1 SOL</span>
                                                        <span className="text-yellow-400">Max: {Math.min(project.targetAmount - project.raisedAmount, balance).toFixed(1)} SOL</span>
                                                    </div>

                                                    {/* Quick amount buttons */}
                                                    <div className="mt-3">
                                                        <p className="text-blue-200 text-xs mb-2">Quick amounts:</p>
                                                        <div className="flex gap-2 flex-wrap">
                                                            {[0.5, 1, 2, 5, 10].map((amount) => {
                                                                const maxAmount = Math.min(project.targetAmount - project.raisedAmount, balance);
                                                                const isDisabled = amount > maxAmount || amount < 0.1;
                                                                return (
                                                                    <button
                                                                        key={amount}
                                                                        onClick={() => setInvestmentAmount(amount)}
                                                                        disabled={isDisabled}
                                                                        className={`px-3 py-1 rounded text-xs transition-colors ${isDisabled
                                                                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                                                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                                                                            }`}
                                                                    >
                                                                        {amount} SOL
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex gap-3">
                                                    <button
                                                        onClick={() => {
                                                            if (investmentAmount >= 0.1 &&
                                                                investmentAmount <= (project.targetAmount - project.raisedAmount) &&
                                                                investmentAmount <= balance) {
                                                                simulateInvestment("You", project.id, investmentAmount);
                                                                // Update wallet balance
                                                                updateBalance(balance - investmentAmount);
                                                                setShowInvestmentModal(false);
                                                                setInvestmentAmount(0);
                                                            }
                                                        }}
                                                        disabled={investmentAmount < 0.1 ||
                                                            investmentAmount > (project.targetAmount - project.raisedAmount) ||
                                                            investmentAmount > balance}
                                                        className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors"
                                                    >
                                                        💰 Invest {investmentAmount} SOL
                                                    </button>
                                                    <button
                                                        onClick={() => setShowInvestmentModal(false)}
                                                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </motion.div>
                            </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default SolanaSimulation;


